import {
  AdjacentType,
  DatastoreObjectType,
  DatastoreObjectTypes,
  EdgeParams,
  ErrData,
  ErrDataPromise,
  Traversal,
  ValidBusinessObject,
  ValidBusinessObjectList,
} from '@iotv/iotv-v3-types'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../util/lambda/debug'
import { getAllParentEdgeAttributeDefinitions, getParentEdgeTypeId } from '../classDependentTypes/EdgeTypes/edgeTypeMatrixFns'
import { ModelClassNames } from '../classDependentTypes/ModelClassNames'
import { BranchingQueryDefinition, IBranchingQueryDefinition } from '../util/dynamodb/GraphQuery/BranchingQueryDefinition'
import { edgeTypeMatrix } from '../classDependentTypes/EdgeTypes/EdgeTypeMatrix'
import { GetBranchingQueryDefinitionInput } from './types'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

type GetAscendingGroupRecursiveQueryInput = {
  /** the type of object that 'has group' */
  groupDefiningObjectTypeId: string

  /** the items to start the path from */
  item: ValidBusinessObject
}

/** a development by-product */
/** returns type if all items of same time if not throws  error*/
const getUniqueObjectTypeId = (items: ValidBusinessObjectList): string | undefined => {
  /** ensures every item is the type to be used  */
  const itemType = items.reduce(
    (a, c) => {
      const thisType = c.type
      if (a && a !== thisType) {
        throw new Error(`More that one item type included in list`)
      } else {
        a = thisType
      }
      return a
    },
    undefined as string | undefined,
  )

  return itemType
}

type GetItemTypeToGroupNestedQueryDefInput = {
  /** the type of object that 'has group' */
  groupDefiningObjectTypeId: string

  /** the item type to start the path from */
  sourceItemType: string
  /** whether to add a  prefix to query ids, to avoid duplication when used in composite functions, default true */
  addIdPrefixes?: boolean
  finalSubQueries?: BranchingQueryDefinition[]
}

export const getItemTypeToGroupNestedAscendingQueryDef = ({
  groupDefiningObjectTypeId,
  sourceItemType,
  addIdPrefixes = true,
  finalSubQueries = [],
}: GetItemTypeToGroupNestedQueryDefInput): BranchingQueryDefinition => {
  const idPrefix: string = addIdPrefixes ? `${sourceItemType}-${sourceItemType}-${Traversal.ASCENDING}-` : ''

  const groupToGroupDefiningType: EdgeParams[] = [
    { objectTypeId: groupDefiningObjectTypeId, adjacencyType: AdjacentType.PARENT, edgeTypeId: getParentEdgeTypeId('Group', groupDefiningObjectTypeId) },
  ]

  const groupToGroupDefiningTypeQuery: BranchingQueryDefinition = new BranchingQueryDefinition({
    id: `${idPrefix}groupToGroupDefiningTypeQuery`,
    path: groupToGroupDefiningType,
    subQueries: finalSubQueries,
  })

  const groupDefiningTypeToGroup: EdgeParams[] = [
    { objectTypeId: 'Group', adjacencyType: AdjacentType.PARENT, edgeTypeId: getParentEdgeTypeId(sourceItemType, 'Group') },
  ]

  const groupDefiningTypeToGroupQuery: BranchingQueryDefinition = new BranchingQueryDefinition({
    id: `${idPrefix}groupDefiningTypeToGroupQuery`,
    path: groupDefiningTypeToGroup,
    subQueries: [],
  })

  const groupToGroup: EdgeParams[] = [
    { objectTypeId: 'Group', adjacencyType: AdjacentType.PARENT, edgeTypeId: edgeTypeMatrix.Group.Group.group_has_subGroup.edgeTypeId },
  ]

  const groupToGroupQuery: BranchingQueryDefinition = new BranchingQueryDefinition({
    id: `${idPrefix}groupToGroupQuery`,
    path: groupToGroup,
    subQueries: [groupToGroupDefiningTypeQuery],
  })

  groupToGroupQuery.addSubQuery(groupToGroupQuery)
  groupToGroupDefiningTypeQuery.addSubQuery(groupDefiningTypeToGroupQuery)

  groupDefiningTypeToGroupQuery.addSubQuery(groupToGroupDefiningTypeQuery)
  groupDefiningTypeToGroupQuery.addSubQuery(groupToGroupQuery)

  const subjectQuery: EdgeParams[] = [{ objectTypeId: sourceItemType, adjacencyType: AdjacentType.SUBJECT }]

  const itemsToGroupQuery = new BranchingQueryDefinition({
    path: subjectQuery,
    subQueries: [groupDefiningTypeToGroupQuery, groupToGroupDefiningTypeQuery],
    id: `${idPrefix}itemTypeToGroupNestedAscendingQueryBase`,
  })

  return itemsToGroupQuery
}

type GetClosestTypeToGroupNestedQueryDefInput = GetItemTypeToGroupNestedQueryDefInput & {
  finalSubQueries?: BranchingQueryDefinition[]
}

/**
 * Gets path from `sourceItem` -> `source Item group` -> `...` -> `source Item Group` -> `GroupDefiningObject`
 *
 * Stops when it hits the first group defining object.
 * Useful when you want to find the nearest ascending item, but go no further:
 *
 * E.g. User -> Relevant Customer
 *
 * Final Sub Queries allows subqueries to be injected into the final query of group -> groupDefiningType, so you can chain them like normal
 */
export const getClosestItemTypeToGroupNestedAscendingQueryDef = ({
  groupDefiningObjectTypeId,
  sourceItemType,
  addIdPrefixes = true,
  finalSubQueries = [],
}: GetClosestTypeToGroupNestedQueryDefInput): BranchingQueryDefinition => {
  const idPrefix: string = addIdPrefixes ? `${sourceItemType}-${sourceItemType}-${Traversal.ASCENDING}-` : ''

  // There are a rare few objects that go straight from themselves to customer (e.g. property). This query lets those objects to be inputs
  const sourceToDefiningTypeQuery: BranchingQueryDefinition = new BranchingQueryDefinition({
    id: `${idPrefix}sourceToDefiningTypeQuery`,
    path: [
      {
        objectTypeId: groupDefiningObjectTypeId,
        adjacencyType: AdjacentType.PARENT,
        edgeTypeId: getParentEdgeTypeId(sourceItemType, groupDefiningObjectTypeId),
      },
    ],
    subQueries: [],
  })

  const groupToGroupDefiningTypeQuery: BranchingQueryDefinition = new BranchingQueryDefinition({
    id: `${idPrefix}groupToGroupDefiningTypeQuery`,
    path: [
      { objectTypeId: groupDefiningObjectTypeId, adjacencyType: AdjacentType.PARENT, edgeTypeId: getParentEdgeTypeId('Group', groupDefiningObjectTypeId) },
    ],
    subQueries: finalSubQueries,
  })

  const groupToGroupQuery: BranchingQueryDefinition = new BranchingQueryDefinition({
    id: `${idPrefix}groupToGroupQuery`,
    path: [{ objectTypeId: 'Group', adjacencyType: AdjacentType.PARENT, edgeTypeId: edgeTypeMatrix.Group.Group.group_has_subGroup.edgeTypeId }],
    subQueries: [groupToGroupDefiningTypeQuery],
  })

  groupToGroupQuery.addSubQuery(groupToGroupQuery)

  const itemsToGroupQuery = new BranchingQueryDefinition({
    path: [{ objectTypeId: 'Group', adjacencyType: AdjacentType.PARENT, edgeTypeId: getParentEdgeTypeId(sourceItemType, 'Group') }],
    subQueries: [groupToGroupDefiningTypeQuery, groupToGroupQuery],
    id: `${idPrefix}itemsToGroup`,
  })

  // Start with myself so the source can be a group too
  const closestAscendingApexQuery = new BranchingQueryDefinition({
    id: `${idPrefix}itemTypeToGroupNestedAscendingQueryBase`,
    path: [{ objectTypeId: sourceItemType, adjacencyType: AdjacentType.SUBJECT }],
    subQueries: [itemsToGroupQuery, groupToGroupQuery, groupToGroupDefiningTypeQuery, sourceToDefiningTypeQuery],
  })

  return closestAscendingApexQuery
}

/** returns branching query definition that can be used in (datastore) getBranchingQueryDefinitionResults and will return a nodeEdgeMapper containing the upwards path via Groups and groupDefiningTypeId */
export const getAscendingGroupRecuriveQuery = ({ groupDefiningObjectTypeId, item }: GetAscendingGroupRecursiveQueryInput): GetBranchingQueryDefinitionInput => {
  const query: GetBranchingQueryDefinitionInput = {
    topLevelQuery: getItemTypeToGroupNestedAscendingQueryDef({ groupDefiningObjectTypeId, sourceItemType: item.type }),
    contextObjects: [item],
  }

  return query
}
